import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import $api from "@/frontend/shared/api/axios";
import { useQueryParams } from "@/frontend/shared/lib/hooks/useQueryParams";
import { Input } from "@/frontend/shared/ui/Input";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { Typography } from "@/frontend/shared/ui/Typography";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { getRouteSignIn } from "@/frontend/shared/const/router";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { AxiosError } from "axios";

type ResetPasswordPayload = {
  key: string;
  password: string;
};

// TODO: should be importable from a common lib - J
const PasswordSchema = z.string().min(8).max(32);

export function ResetPasswordPage() {
  const navigate = useNavigate();

  const queryParams = useQueryParams();
  const key = queryParams.get("key") as string;

  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const isPasswordsMatch = password === confirmPassword;
  const isPasswordValid = isPasswordsMatch && PasswordSchema.safeParse(password).success;

  const mutation = useMutation({
    mutationFn: async ({ key, password }: ResetPasswordPayload) => {
      const response = await $api.post("/auth/passwords/reset", { key, password });
      return response.data;
    },
    onSuccess: () => {
      snackbarConstructor("Great! Please log in with your new password", "normal");
      setPassword("");
      setConfirmPassword("");
      navigate(getRouteSignIn());
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to reset password. Try again later or contact the administrator.";
      snackbarConstructor(message, "error");
      reportErrorToSentry(message, "/auth/passwords/reset");
    }
  });

  const onChangeSetPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setPassword(e.target.value);
  };

  const onChangeSetConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setConfirmPassword(e.target.value);
  };

  const resetPassword = async () => {
    await mutation.mutateAsync({ key, password });
  };

  return (
    <form
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        width: "100vw",
        height: "calc(100vh - 40px)"
      }}
      onSubmit={(e) => {
        e.preventDefault();
        resetPassword();
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "200px" }}>
        <Typography variant="heading-5" style={{ marginBottom: "0.5rem" }}>
          Create a new password
        </Typography>
        <Input
          type="password"
          placeholder="New password"
          style={{ width: "100%", fontSize: 18 }}
          value={password}
          onChange={onChangeSetPassword}
        />
        <Input
          type="password"
          placeholder="Confirm new password"
          style={{ width: "100%", fontSize: 18 }}
          value={confirmPassword}
          onChange={onChangeSetConfirmPassword}
        />
      </div>

      <NormalButton
        type="submit"
        style={{ width: "200px" }}
        size="medium"
        isDisabled={!password || !isPasswordValid || mutation.isPending}
      >
        Submit
      </NormalButton>
    </form>
  );
}
